import { idb, TOKO } from './idb';
import { api, GalatJaringan, GalatApi } from './api';
import type { OrderInput, PaymentInput } from '@santap/shared';

/**
 * Antrean keluar — jantung kerja offline.
 *
 * Setiap tindakan yang mengubah uang ditulis ke sini LEBIH DULU, baru dicoba
 * dikirim. Kalau pengiriman gagal karena jaringan, tindakan itu tetap tersimpan
 * dan dicoba lagi nanti. Kasir tidak pernah menunggu server.
 *
 * Yang membuat ini aman adalah tiga hal yang sudah ditanam di lapisan lain:
 *   - ID dibuat di perangkat, jadi kiriman ulang mengenai baris yang sama
 *   - Server idempoten, jadi kiriman kelima sama hasilnya dengan yang pertama
 *   - Harga dibekukan saat pesanan dibuat, jadi keterlambatan kirim tidak
 *     mengubah angka
 *
 * Yang TIDAK dilakukan di sini: menebak. Kalau server menolak sebuah pesanan
 * karena alasan bisnis (total tidak cocok, katalog basi), antreannya berhenti
 * mencoba dan menandainya untuk dilihat manusia. Mencoba terus-menerus hanya
 * menyembunyikan masalah yang butuh keputusan.
 */

export type JenisAntrean = 'pesanan' | 'pembayaran';

export interface ItemAntrean {
  id: string;
  jenis: JenisAntrean;
  muatan: OrderInput | PaymentInput;
  dibuatPada: string;
  percobaan: number;
  terakhirCoba?: string;
  /** Diisi kalau server menolak karena alasan bisnis — butuh perhatian manusia. */
  ditolak?: { kode: string; pesan: string };
}

type Pendengar = (keadaan: KeadaanAntrean) => void;

export interface KeadaanAntrean {
  menunggu: number;
  ditolak: number;
  sedangKirim: boolean;
  tertuaMenit: number;
  online: boolean;
}

const pendengar = new Set<Pendengar>();
let sedangKirim = false;
let timer: number | undefined;

export function dengarkan(fn: Pendengar): () => void {
  pendengar.add(fn);
  void kabari();
  return () => pendengar.delete(fn);
}

async function kabari() {
  const semua = await idb.semua<ItemAntrean>(TOKO.outbox);
  const menunggu = semua.filter((i) => !i.ditolak);
  const tertua = menunggu.reduce<number>((a, i) => {
    const menit = (Date.now() - new Date(i.dibuatPada).getTime()) / 60000;
    return Math.max(a, menit);
  }, 0);

  const keadaan: KeadaanAntrean = {
    menunggu: menunggu.length,
    ditolak: semua.length - menunggu.length,
    sedangKirim,
    tertuaMenit: Math.floor(tertua),
    online: navigator.onLine,
  };
  pendengar.forEach((p) => p(keadaan));
}

/** Masukkan ke antrean, lalu coba kirim. Tidak pernah melempar galat jaringan. */
export async function antrekan(jenis: JenisAntrean, muatan: OrderInput | PaymentInput) {
  const item: ItemAntrean = {
    id: muatan.id,
    jenis,
    muatan,
    dibuatPada: new Date().toISOString(),
    percobaan: 0,
  };
  await idb.set(TOKO.outbox, item);
  await kabari();
  void kirimSemua();
  return item;
}

/**
 * Kirim seluruh antrean, paling tua dulu.
 *
 * Urutan penting: pembayaran merujuk pesanan, jadi pesanan harus sampai lebih
 * dulu. Karena antrean diurutkan berdasarkan waktu pembuatan, urutan itu
 * terjaga dengan sendirinya.
 */
export async function kirimSemua(): Promise<{ terkirim: number; gagal: number }> {
  if (sedangKirim || !navigator.onLine) return { terkirim: 0, gagal: 0 };
  sedangKirim = true;
  await kabari();

  let terkirim = 0, gagal = 0;
  try {
    const semua = (await idb.semua<ItemAntrean>(TOKO.outbox))
      .filter((i) => !i.ditolak)
      .sort((a, b) => a.dibuatPada.localeCompare(b.dibuatPada));

    for (const item of semua) {
      try {
        if (item.jenis === 'pesanan') {
          await api.kirimPesanan(item.muatan as OrderInput);
        } else {
          await api.bayar(item.muatan as PaymentInput);
        }
        await idb.del(TOKO.outbox, item.id);
        terkirim++;
      } catch (e) {
        if (e instanceof GalatJaringan) {
          // Jaringan putus lagi. Berhenti; sisanya menunggu giliran berikutnya.
          break;
        }
        if (e instanceof GalatApi && e.status >= 400 && e.status < 500) {
          // Server menolak karena alasan bisnis. Mencoba lagi tidak akan
          // mengubah apa pun — tandai untuk dilihat manusia.
          await idb.set(TOKO.outbox, {
            ...item, ditolak: { kode: e.kode, pesan: e.message },
            percobaan: item.percobaan + 1, terakhirCoba: new Date().toISOString(),
          });
          gagal++;
          continue;
        }
        // Galat server (5xx). Coba lagi nanti.
        await idb.set(TOKO.outbox, {
          ...item, percobaan: item.percobaan + 1, terakhirCoba: new Date().toISOString(),
        });
        gagal++;
      }
    }
  } finally {
    sedangKirim = false;
    await kabari();
  }
  return { terkirim, gagal };
}

export async function daftarDitolak(): Promise<ItemAntrean[]> {
  const semua = await idb.semua<ItemAntrean>(TOKO.outbox);
  return semua.filter((i) => i.ditolak);
}

/** Buang satu item yang ditolak, setelah manusia memutuskan. */
export async function buang(id: string) {
  await idb.del(TOKO.outbox, id);
  await kabari();
}

/**
 * Mulai mesin antrean.
 *
 * Dua pemicu: kembalinya jaringan, dan denyut berkala. Denyut tetap ada karena
 * `navigator.onLine` berbohong — ia hanya tahu ada antarmuka jaringan aktif,
 * bukan apakah server benar-benar bisa dihubungi. Wi-Fi warung yang tersambung
 * tapi tidak sampai ke internet adalah keadaan yang sangat lazim.
 */
export function mulaiMesin() {
  const picu = () => { void kirimSemua(); };
  window.addEventListener('online', () => { void kabari(); picu(); });
  window.addEventListener('offline', () => { void kabari(); });
  timer = window.setInterval(picu, 15_000);
  picu();
}

export function hentikanMesin() {
  if (timer) window.clearInterval(timer);
}
